import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Download, FileText, ChevronLeft, ChevronRight, Pencil, X } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const CODE_CLS = {
  P: "bg-green-100 text-green-700", "P-": "bg-amber-100 text-amber-700", "P+": "bg-indigo-100 text-indigo-700",
  "P*": "bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-500 font-black",
  FE: "bg-blue-100 text-blue-700", MA: "bg-red-100 text-red-700",
  PR: "bg-orange-100 text-orange-700", EF: "bg-teal-100 text-teal-700", ST: "bg-purple-100 text-purple-700",
  AS: "bg-gray-200 text-gray-600", "104": "bg-fuchsia-100 text-fuchsia-700", LU: "bg-slate-200 text-slate-600",
  CM: "bg-pink-100 text-pink-700", CP: "bg-pink-100 text-pink-700", F: "bg-rose-200 text-rose-800", R: "bg-gray-50 text-gray-300",
};
const TOTS = [
  ["presenze", "Pre"], ["straordinario_ore", "Str"], ["ore_esenti", "Ese"], ["festivi_lavorati", "FestL"], ["permesso_pres_ore", "PmP"], ["ferie", "Fe"], ["malattia", "Ma"], ["permesso_ore", "ROL"],
  ["exfest_ore", "ExF"], ["studio_ore", "Stu"], ["l104", "104"], ["aspettativa", "Asp"],
];

const TOT_TITLES = {
  presenze: "Giorni di presenza", straordinario_ore: "Totale ore straordinario", ore_esenti: "Totale ore oltre le 8h non conteggiate come straordinario (livelli esenti)", festivi_lavorati: "Giorni festivi lavorati (domeniche e festività)", permesso_pres_ore: "Totale ore permesso da presenza (< 8h)",
  ferie: "Giorni di ferie", malattia: "Giorni di malattia", permesso_ore: "Ore permesso ROL",
  exfest_ore: "Ore ex festività", studio_ore: "Ore permesso studio", l104: "Giorni Legge 104", aspettativa: "Giorni aspettativa",
};
const hhmmRome = (iso) => { try { return new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Rome" }).format(new Date(iso)); } catch { return ""; } };

export const MonthlyGridTab = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfing, setPdfing] = useState(false);
  const [editCell, setEditCell] = useState(null); // {employeeId, name, date}
  const [editForm, setEditForm] = useState({ check_in: "", check_out: "", pauses: [] });
  const [editBusy, setEditBusy] = useState(false);
  const [otExporting, setOtExporting] = useState(false);
  const [otPdfing, setOtPdfing] = useState(false);
  const [company, setCompany] = useState("all");
  const [dept, setDept] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/monthly-grid?year=${year}&month=${month}&company=${company}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [year, month, company]);
  useEffect(() => { load(); }, [load]);

  const depts = useMemo(() => Array.from(new Set((data?.rows || []).map((r) => r.dept).filter(Boolean))).sort(), [data]);
  const rows = useMemo(() => (data?.rows || []).filter((r) => dept === "all" || r.dept === dept), [data, dept]);

  const openEdit = async (employeeId, name, dayNum) => {
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    setEditCell({ employeeId, name, date: dateStr });
    setEditForm({ check_in: "", check_out: "", pauses: [] });
    try {
      const r = await api.get(`/attendance/admin/${employeeId}/${dateStr}`);
      const rec = r.data || {};
      const pauses = (rec.pauses || []).map((p) => ({ start: p.start ? hhmmRome(p.start) : "", end: p.end ? hhmmRome(p.end) : "" }));
      setEditForm({
        check_in: rec.check_in ? hhmmRome(rec.check_in) : "",
        check_out: rec.check_out ? hhmmRome(rec.check_out) : "",
        pauses,
      });
    } catch { /* nuova giornata */ }
  };

  const addPause = () => setEditForm((f) => ({ ...f, pauses: [...f.pauses, { start: "", end: "" }] }));
  const removePause = (idx) => setEditForm((f) => ({ ...f, pauses: f.pauses.filter((_, i) => i !== idx) }));
  const setPause = (idx, key, val) => setEditForm((f) => ({ ...f, pauses: f.pauses.map((p, i) => (i === idx ? { ...p, [key]: val } : p)) }));

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const payload = {
        check_in: editForm.check_in || null,
        check_out: editForm.check_out || null,
        pauses: editForm.pauses.filter((p) => p.start && p.end),
      };
      await api.put(`/attendance/admin/${editCell.employeeId}/${editCell.date}`, payload);
      toast.success("Timbratura aggiornata");
      setEditCell(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setEditBusy(false); }
  };

  const exportOvertime = async () => {
    setOtExporting(true);
    try {
      const res = await api.get(`/attendance/overtime/export?year=${year}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = `straordinari_autorizzati_${year}_${String(month).padStart(2, "0")}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export straordinari scaricato");
    } catch { toast.error("Errore nell'export straordinari"); } finally { setOtExporting(false); }
  };

  const exportOvertimePdf = async () => {
    setOtPdfing(true);
    try {
      const res = await api.get(`/attendance/overtime/export-pdf?year=${year}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `straordinari_${year}_${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF straordinari scaricato");
    } catch { toast.error("Errore nel PDF straordinari"); } finally { setOtPdfing(false); }
  };

  const prev = () => { if (month === 1) { setMonth(12); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear((y) => y + 1); } else setMonth((m) => m + 1); };

  const exportCsv = () => {
    if (!data) return;
    const head = ["Matricola", "Dipendente", "Reparto", ...data.day_headers.map((d) => d.day), ...TOTS.map((t) => t[1])];
    const lines = [head.join(";")];
    rows.forEach((r) => {
      lines.push([r.matricola, r.name, r.dept, ...r.days.map((c) => c || ""), ...TOTS.map((t) => r.totals[t[0]])].join(";"));
    });
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `presenze_${year}_${String(month).padStart(2, "0")}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    setPdfing(true);
    try {
      const res = await api.get(`/reports/monthly-grid/pdf?year=${year}&month=${month}&company=${company}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `prospetto_presenze_${year}_${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel PDF"); } finally { setPdfing(false); }
  };

  return (
    <div data-testid="monthly-grid-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="grid-prev" className="w-8 h-8 flex items-center justify-center rounded-sm border border-gray-300 hover:bg-gray-100 transition-colors"><ChevronLeft size={16} /></button>
          <span className="font-black text-[#16345c] min-w-[160px] text-center text-[15px]">{MONTHS[month - 1]} {year}</span>
          <button onClick={next} data-testid="grid-next" className="w-8 h-8 flex items-center justify-center rounded-sm border border-gray-300 hover:bg-gray-100 transition-colors"><ChevronRight size={16} /></button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="export-grid-csv" className="flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-3 py-2 rounded-sm transition-colors"><Download size={15} /> CSV</button>
          <button onClick={exportOvertime} disabled={otExporting} data-testid="export-overtime-csv" className="flex items-center gap-2 border border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white text-[13px] font-bold px-3 py-2 rounded-sm transition-colors disabled:opacity-60"><Download size={15} /> {otExporting ? "..." : "Straordinari"}</button>
          <button onClick={exportOvertimePdf} disabled={otPdfing} data-testid="export-overtime-pdf" className="flex items-center gap-2 border border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white text-[13px] font-bold px-3 py-2 rounded-sm transition-colors disabled:opacity-60"><FileText size={15} /> {otPdfing ? "..." : "Straord. PDF"}</button>
          <button onClick={exportPdf} disabled={pdfing} data-testid="export-grid-pdf" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-3 py-2 rounded-sm transition-colors disabled:opacity-60"><FileText size={15} /> {pdfing ? "..." : "Prospetto PDF"}</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={company} onChange={(e) => { setCompany(e.target.value); setDept("all"); }} data-testid="grid-filter-company" className="border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-[#16345c] bg-white">
          <option value="all">Tutte le società</option>
          <option value="teleborsa">Teleborsa srl</option>
          <option value="sit">S.I.T. srl</option>
        </select>
        <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="grid-filter-dept" className="border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-[#16345c] bg-white">
          <option value="all">Tutti i reparti</option>
          {depts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <span className="text-[12px] text-gray-400">{rows.length} dipendenti</span>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">Caricamento...</div>
      ) : !data || rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Nessun dipendente da mostrare.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="text-[11px] border-collapse w-full">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-[#16345c] text-white text-left px-2 py-2 font-bold min-w-[150px]">Dipendente</th>
                {data.day_headers.map((d) => (
                  <th key={d.day} className={`px-1 py-2 font-bold text-center ${d.holiday ? "bg-[#9d174d] text-white" : d.weekend ? "bg-[#c0392b] text-white" : "bg-[#16345c] text-white"}`} style={{ minWidth: 22 }} title={d.holiday ? "Festività" : ""}>{d.day}</th>
                ))}
                {TOTS.map((t) => <th key={t[0]} title={TOT_TITLES[t[0]] || t[1]} className="px-1 py-2 font-bold text-center bg-[#0d6cb8] text-white" style={{ minWidth: 30 }}>{t[1]}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id} className="border-b border-gray-100">
                  <td className="sticky left-0 z-10 bg-white px-2 py-1.5 border-r border-gray-200">
                    <div className="font-bold text-[#16345c] text-[12px] truncate max-w-[150px]">{r.name}</div>
                    <div className="text-[10px] text-gray-400">{r.matricola} · {r.dept}</div>
                  </td>
                  {r.days.map((c, i) => (
                    <td
                      key={i}
                      onClick={() => openEdit(r.employee_id, r.name, i + 1)}
                      data-testid={`grid-cell-${r.employee_id}-${i + 1}`}
                      title="Modifica timbratura"
                      className={`text-center px-0.5 py-1.5 border-r border-gray-100 font-semibold cursor-pointer hover:ring-2 hover:ring-inset hover:ring-[#0d6cb8] ${CODE_CLS[c] || ""}`}
                    >{c}</td>
                  ))}
                  {TOTS.map((t) => <td key={t[0]} className="text-center px-1 py-1.5 border-r border-gray-100 font-bold text-[#16345c] bg-blue-50/40">{r.totals[t[0]]}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#eef4fb] border-t-2 border-[#16345c]" data-testid="grid-totals-row">
                <td className="sticky left-0 z-10 bg-[#eef4fb] px-2 py-2 font-black text-[#16345c] text-[11px] uppercase">Totali</td>
                <td colSpan={data.day_headers.length} className="bg-[#eef4fb]" />
                {TOTS.map((t) => {
                  const sum = rows.reduce((s, r) => s + (Number(r.totals[t[0]]) || 0), 0);
                  return <td key={t[0]} className="text-center px-1 py-2 font-black text-[#16345c] bg-[#dce9f7]">{Math.round(sum * 10) / 10}</td>;
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {data && (
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(data.legend).map(([k, v]) => (
            <span key={k} className={`text-[10.5px] px-2 py-0.5 rounded-full font-semibold ${CODE_CLS[k] || "bg-gray-100 text-gray-600"}`}>{k} = {v}</span>
          ))}
        </div>
      )}

      {editCell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="attendance-edit-modal" onClick={() => setEditCell(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="text-[15px] font-bold text-[#16345c]">Modifica timbratura</h3>
              <button onClick={() => setEditCell(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-[13px] text-gray-600"><span className="font-semibold text-[#16345c]">{editCell.name}</span> · {editCell.date.split("-").reverse().join("/")}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Entrata</label>
                  <input type="time" value={editForm.check_in} onChange={(e) => setEditForm((f) => ({ ...f, check_in: e.target.value }))} data-testid="edit-check-in" className="w-full border border-gray-300 rounded-sm px-2 py-2 text-[14px] outline-none focus:border-[#16345c]" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Uscita</label>
                  <input type="time" value={editForm.check_out} onChange={(e) => setEditForm((f) => ({ ...f, check_out: e.target.value }))} data-testid="edit-check-out" className="w-full border border-gray-300 rounded-sm px-2 py-2 text-[14px] outline-none focus:border-[#16345c]" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[12px] font-semibold text-gray-600">Pause</label>
                  <button onClick={addPause} data-testid="edit-add-pause" className="text-[12px] font-semibold text-indigo-600 hover:text-indigo-800">+ Aggiungi pausa</button>
                </div>
                {editForm.pauses.length === 0 && <div className="text-[12px] text-gray-400 italic">Nessuna pausa. Usa "Aggiungi pausa" per inserirne una o più.</div>}
                <div className="space-y-2">
                  {editForm.pauses.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`edit-pause-row-${idx}`}>
                      <input type="time" value={p.start} onChange={(e) => setPause(idx, "start", e.target.value)} data-testid={`edit-pause-start-${idx}`} className="flex-1 border border-gray-300 rounded-sm px-2 py-1.5 text-[13px] outline-none focus:border-[#16345c]" />
                      <span className="text-gray-400 text-[13px]">→</span>
                      <input type="time" value={p.end} onChange={(e) => setPause(idx, "end", e.target.value)} data-testid={`edit-pause-end-${idx}`} className="flex-1 border border-gray-300 rounded-sm px-2 py-1.5 text-[13px] outline-none focus:border-[#16345c]" />
                      <button onClick={() => removePause(idx)} data-testid={`edit-pause-remove-${idx}`} title="Rimuovi pausa" className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-[#c00510] hover:bg-red-50"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[11.5px] text-gray-400">Lascia entrata/uscita vuote per rimuovere la timbratura. La prestazione netta (escluse le pause) e lo stato P / permesso / straordinario vengono ricalcolati automaticamente.</p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
              <button onClick={() => setEditCell(null)} className="text-[13px] font-semibold text-gray-500 hover:text-gray-800 px-3 py-2">Annulla</button>
              <button onClick={saveEdit} disabled={editBusy} data-testid="edit-save" className="bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm disabled:opacity-60">{editBusy ? "Salvataggio..." : "Salva"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
